import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  TrashIcon,
  UserPlusIcon,
  AdjustmentsHorizontalIcon,
} from "@heroicons/react/24/outline";
import CampaignForm from "./ManageCampaignComponents/CampaignForm";
import AssignTargetPopup from "./ManageCampaignComponents/AssignTargetPopup";
import { Switch } from "@headlessui/react";

const API_URL = process.env.REACT_APP_API_URL || 'https://didrouteapi.com/api';

const ManageCampaign = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [availableNumbers, setAvailableNumbers] = useState([]);

  // Fetch campaigns on component mount
  useEffect(() => {
    fetchCampaigns();
    fetchNumbers();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/campaigns`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // Make sure weight_flag is included in the campaign data
        const campaignsWithFlags = data.data.map(campaign => ({
          ...campaign,
          weight_flag: campaign.weight_flag || 'No'
        }));
        setCampaigns(campaignsWithFlags);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const fetchNumbers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/numbers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const result = await response.json();

      if (result.success && result.data && result.data.data) {
        const numbers = Array.isArray(result.data.data) ? result.data.data : [];
        setAvailableNumbers(numbers);
      } else {
        setAvailableNumbers([]);
      }
    } catch (error) {
      setAvailableNumbers([]);
    }
  };

  const handleEdit = (campaign) => {
    const campaignWithFlags = {
      ...campaign,
      weight_flag: campaign.weight_flag || 'No',
      recording: campaign.recording || 'Yes'
    };
    setSelectedCampaign(campaignWithFlags);
    setShowForm(true);
  };

  const handleAssignTarget = (campaign) => {
    setSelectedCampaign(campaign);
    setShowAssignPopup(true);
  };

  const handleStatusChange = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/campaigns/${id}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (data.success) {
        // Update the campaigns list with the new status
        setCampaigns(campaigns.map(campaign => 
          campaign.id === id 
            ? { ...campaign, status: data.data.status } 
            : campaign
        ));
      }
    } catch (error) {
      console.error('Error toggling status:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/campaigns/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success) {
          fetchCampaigns(); // Refresh campaigns after deletion
        }
      } catch (error) {
        console.error('Error deleting campaign:', error);
      }
    }
  };

  const handleSave = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      const method = selectedCampaign ? 'PUT' : 'POST';
      const url = selectedCampaign 
        ? `${API_URL}/campaigns/${selectedCampaign.id}`
        : `${API_URL}/campaigns`;

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        // Refresh the campaigns list
        await fetchCampaigns();
        setShowForm(false);
        setSelectedCampaign(null);
      }
    } catch (error) {
      console.error('Error saving campaign:', error);
    }
  };

  const filteredCampaigns = campaigns.filter(
    (campaign) =>
      campaign.campaign_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      campaign.country.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="bg-white p-4 pt-8 rounded-lg shadow-lg">
      {/* Header Section */}
      <div className="p-2 mb-3 rounded-lg flex justify-between items-center">
        <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Manage Campaigns
        </h1>

        {/* Add Campaign Button */}
        <button
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg"
          onClick={() => setShowForm(true)}
        >
          Add Campaign
        </button>
      </div>

      {/* Search Input */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by campaign name or country..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className=" text-gray-900 text-xs px-2 py-2 border border-gray-700 placeholder:text-gray-900 rounded-lg w-full"
        />
      </div>

      {/* Modals */}
      {showForm && (
        <CampaignForm
          campaign={selectedCampaign}
          onClose={() => {
            setShowForm(false);
            setSelectedCampaign(null);
          }}
          onSave={handleSave}
          availableNumbers={availableNumbers}
        />
      )}
      {showAssignPopup && (
        <AssignTargetPopup
          campaign={selectedCampaign}
          onClose={() => setShowAssignPopup(false)}
          onUpdateTarget={(updatedTargets) => {
            console.log("Updated Targets:", updatedTargets);
            setShowAssignPopup(false);
          }}
        />
      )}

      {/* Campaign Table */}
      <div className="bg-white p-2 rounded-lg overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full divide-y divide-gray-700">
            <thead>
              <tr>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Name
                </th>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Country
                </th>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Number
                </th>
                
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Live
                </th>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Today
                </th>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Weekly
                </th>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Monthly
                </th>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Status
                </th>
                <th className="py-3.5 px-3 text-left text-xs font-medium text-black whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {filteredCampaigns.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    {campaign.campaign_name}
                  </td>
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    {campaign.country}
                  </td>
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    {campaign.target_number}
                  </td>
                  
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    {campaign.live}
                  </td>
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    {campaign.today}
                  </td>
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    {campaign.hourly}
                  </td>
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    {campaign.monthly}
                  </td>
                  <td className="whitespace-nowrap py-4 px-3 text-xs text-gray-900">
                    <Switch
                      checked={campaign.status === "active"}
                      onChange={() => handleStatusChange(campaign.id)}
                      className={`${
                        campaign.status === "active"
                          ? "bg-green-600"
                          : "bg-gray-400"
                      } relative inline-flex h-4 w-8 items-center rounded-full transition-colors focus:outline-none`}
                    >
                      <span
                        className={`${
                          campaign.status === "active"
                            ? "translate-x-4"
                            : "translate-x-1"
                        } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                  </td>
                  <td className="py-2 flex space-x-2">
                    {/* Edit Button */}
                    <button
                      onClick={() => handleEdit(campaign)}
                      className="text-blue-400 hover:text-blue-500"
                      title="Edit"
                    >
                      <PencilIcon className="w-5 h-5" />
                    </button>
                    {/* Assign Target Button */}
                    <button
                      onClick={() => handleAssignTarget(campaign)}
                      className="text-yellow-400 hover:text-yellow-500"
                      title="Assign Target"
                    >
                      <UserPlusIcon className="w-5 h-5" />
                    </button>
                    {/* Delete Button */}
                    <button
                      onClick={() => handleDelete(campaign.id)}
                      className="text-red-400 hover:text-red-500"
                      title="Delete"
                    >
                      <TrashIcon className="w-5 h-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ManageCampaign;
